import { FC, useState } from 'react';

import lang from '../../lib/language';
import { MarketCapCategory, MarketCapRadarTabEnum } from '../../lib/models/fundamental/types';
import { isEmpty } from '../../lib/util/typeGuards';

import Button from '../Button/Button';
import { ButtonTypesEnum } from '../Button/Button.types';
import DiscoverButton from '../DiscoverButton/DiscoverButton';
import useIsMobile from '../hooks/useIsMobile';
import NoDataAvailable from '../NoDataAvailable/NoDataAvailable';

import MarketCapFilterDropDown from './components/MarketCapFilterDropDown/MarketCapFilterDropDown';
import MarketCapRadarList from './components/MarketCapRadarList/MarketCapRadarList';
import { MarketCapRow } from './MarketCapRadar.types';

import styles from './MarketCapRadar.module.scss';

export type MarketCapRadarProps = {
  gainers: MarketCapRow[];
  losers: MarketCapRow[];
  isReady: boolean;
  isPending: boolean;
  hasMoreThanShownRows: boolean;
  selectedCategory: MarketCapCategory;
  onCategoryChange: (category: MarketCapCategory) => void;
  onSymbolClick: (symbol: string, isGainersSelected: boolean) => void;
  /** Omitted where the app has no market cap radar screen to open. */
  onViewAllClick?: (isGainersSelected: boolean) => void;
};

const MarketCapRadar: FC<MarketCapRadarProps> = ({
  gainers,
  losers,
  isReady,
  isPending,
  hasMoreThanShownRows,
  selectedCategory,
  onCategoryChange,
  onSymbolClick,
  onViewAllClick,
}: MarketCapRadarProps): JSX.Element => {
  const { isMobile } = useIsMobile();
  const [selectedTab, setSelectedTab] = useState<MarketCapRadarTabEnum>(MarketCapRadarTabEnum.Gainers);
  const isGainersSelected = selectedTab === MarketCapRadarTabEnum.Gainers;

  const hasNoAnyData = isEmpty(gainers) && isEmpty(losers) && !isPending;
  const canViewAll = Boolean(onViewAllClick) && !hasNoAnyData && hasMoreThanShownRows;

  const filterDropDown = (
    <MarketCapFilterDropDown
      isDisabled={isPending}
      selectedCategory={selectedCategory}
      handleCategoryChange={onCategoryChange}
    />
  );

  const mobileViewAll = canViewAll && onViewAllClick && (
    <Button
      variant={ButtonTypesEnum.Link}
      onClick={() => onViewAllClick(isGainersSelected)}
      className={styles.viewAllMobile}
    >
      {lang.commonViewAll()}
    </Button>
  );

  return (
    <>
      <div className={styles.titleWrapper}>
        <div className={styles.titleRow}>
          <div className={GlobalClassesEnum.CommonDiscoverTitle}>
            {lang.marketCapRadarTitle()}
          </div>
          {!isMobile && (
            <div className={styles.titleActions}>
              {filterDropDown}
              {canViewAll && onViewAllClick && (
                <DiscoverButton
                  disabled={isPending}
                  onClick={() => onViewAllClick(isGainersSelected)}
                />
              )}
            </div>
          )}
        </div>
        {isMobile && (
          <div className={styles.tabsWrapper}>
            <div className={styles.tabs}>
              <Button
                className={isGainersSelected ? styles.tabActive : styles.tab}
                onClick={() => setSelectedTab(MarketCapRadarTabEnum.Gainers)}
              >
                {lang.commonEpicMoversGainers()}
              </Button>
              <Button
                className={!isGainersSelected ? styles.tabActive : styles.tab}
                onClick={() => setSelectedTab(MarketCapRadarTabEnum.Losers)}
              >
                {lang.commonEpicMoversLosers()}
              </Button>
            </div>
            {filterDropDown}
          </div>
        )}
      </div>
      <div className={isMobile ? styles.wrapperMobile : styles.wrapper}>
        <div className={styles.list}>
          {hasNoAnyData ? (
            <NoDataAvailable
              className={styles.noDataToDisplay}
              isNoDataIcon
              title={lang.commonDiscover52WeekNoDataAll()}
            />
          ) : (
            <>
              {(!isMobile || isGainersSelected) && (
                <MarketCapRadarList
                  title={lang.commonEpicMoversGainers()}
                  data={gainers}
                  isReady={isReady}
                  isPending={isPending}
                  onSymbolClick={symbol => onSymbolClick(symbol, true)}
                  footer={mobileViewAll}
                />
              )}
              {!isMobile && <div className={styles.verticalSpace} />}
              {(!isMobile || !isGainersSelected) && (
                <MarketCapRadarList
                  title={lang.commonEpicMoversLosers()}
                  data={losers}
                  isReady={isReady}
                  isPending={isPending}
                  onSymbolClick={symbol => onSymbolClick(symbol, false)}
                  footer={mobileViewAll}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MarketCapRadar;
